<template>

  <section class="main">

    <!-- Escape / Close Button -->
    <button @click="exitButtonPressed()" id="escape-btn">Close</button>

    <!-- Create Survey Button -->
    <div class="create-btn-container top">
      <button
        v-if="!is_submitting"
        @click="create()"
        :class="{ active: survey_errors.length === 0 }"
        >Save</button>
      <a-spin v-if="is_submitting" size="large" />
    </div>

    <!-- Language Drop-down -->
    <div id="language-dropdown">
      <a-select @change="selectLanguage($event)" :value="survey_language" default-value="en">
        <a-select-option v-for="language of languages" :value="language.key" :key="language.key">{{ language.text }}</a-select-option>
      </a-select>
      <a-tooltip v-if="!is_mobile" placement="right">
        <template slot="title">
          <span>Our bot will display messages in this language.</span>
        </template>
        <a-icon type="info-circle" />
      </a-tooltip>
    </div>

    <!-- Cost Estimator -->
    <div id="cost-estimator" v-if="estimate.total_cost !== '0.00' && !is_wallet_hidden">
      <p>A full completion will cost <b>${{ estimate.total_cost }}</b></p>
      <a-tooltip placement="top">
        <template slot="title">
          <span>Pricing Breakdown 👇</span></br></br>
          <template v-for="(count, answer_type) of estimate.breakdown">
            {{ _.capitalize(answer_type) }}: {{ count }} x {{ prices[answer_type] }} = {{ count * prices[answer_type] }}</br>
          </template>
        </template>
        <a-icon type="info-circle" />
      </a-tooltip>

    </div>


    <div class="container">

      <!-- Emoji Icon -->
      <input :value="survey_emoji" @click.stop="is_emoji_picker_visible=!is_emoji_picker_visible" class="survey-emoji" maxlength="1" type="text" readonly>
      <Picker v-show='is_emoji_picker_visible' @select="pickSurveyEmoji" :native="true" :data="emojiIndex" class="emoji-picker" v-on-clickaway="hideEmojiPicker" />

      <!-- Title Input -->
      <input :value="survey_title" @input="updateSurveyTitle($event)" placeholder="Add a survey title" maxlength="150" class="survey-title" type="text">

      <!-- Starting Message -->
      <StartingMessage :show_errors="show_errors" :is_editing="is_editing"/>

      <!-- Question Blocks -->
      <draggable v-model="questions" v-bind="dragOptions" v-if="!is_mobile">
        <transition-group>
          <QuestionBlock
          v-for="(block, index) in questions" :key="index"
          v-on:focusCard="focusCard"
          :index="index" :is_focused="focused_block_index === index" :block="block" :show_errors="show_errors"/>
        </transition-group>
      </draggable>

      <!-- Mobile Block without Draggable, since overrides touch events (DEBUG) -->
      <QuestionBlock
      v-else
      v-for="(block, index) in questions" :key="index"
      v-on:focusCard="focusCard"
      :index="index" :is_focused="focused_block_index === index" :block="block" :show_errors="show_errors"/>


      <!-- New Question Dropdown Menu -->
      <div class="new-question-container">
        <button @click="is_new_question_dropdown_visible=true">New Question</button>
        <NewQuestionDropdown
        id="new-question-dropdown"
        v-if="is_new_question_dropdown_visible"
        v-on-clickaway="hideNewQuestionDropdown"
        v-on:hide="hideNewQuestionDropdown"
        v-on:focusCard="focusCard" />
      </div>

      <!-- Create Survey Mobile -->
      <div class="create-btn-container bottom mobile">
        <button
          v-if="!is_submitting && questions.length > 2"
          @click="create()"
          :class="{ active: survey_errors.length === 0 }"
          >Save</button>
        <a-spin v-if="is_submitting" size="large" />
      </div>

    </div>

    <div class="bg-fixed"></div>

  </section>

</template>

<script>

import { Picker, EmojiIndex } from 'emoji-mart-vue-fast'
import { mixin as clickaway } from 'vue-clickaway';
import { isMobile, isMobileOnly } from 'mobile-device-detect';
import data from '../components/new_survey/emoji_data.json'
import 'emoji-mart-vue-fast/css/emoji-mart.css'

import _ from 'lodash';

import StartingMessage from '@/components/new_survey/StartingMessage.vue';
import QuestionBlock from '@/components/new_survey/QuestionBlock.vue';
import NewQuestionDropdown from '@/components/new_survey/NewQuestionDropdown.vue';

import draggable from 'vuedraggable';

export default {

  name: 'newSurvey',
  mixins: [ clickaway ],
  components: {

    StartingMessage,
    QuestionBlock,
    NewQuestionDropdown,

    Picker,
    draggable

  },

  data: function () {
    return {
      show_errors: false,
      is_submitting: false,
      is_emoji_picker_visible: false,
      focused_block_index: 0,
      emojiIndex: new EmojiIndex(data),
      is_new_question_dropdown_visible: false
    }
  },

  computed: {

    questions: {
      get: function() {
        return this.$store.state.new_survey.questions;
      },
      set: function(newQuestions) {
        this.$store.commit('rearrangeQuestions', newQuestions);
      }
    },

    survey_emoji() {
      return this.$store.state.new_survey.emoji_icon;
    },

    survey_language() {
      return this.$store.state.new_survey.language;
    },

    survey_title() {
      return this.$store.state.new_survey.title;
    },

    // Ensure the survey is ready to publish.
    survey_errors() {

      let survey_errors = [];
      let survey = this.$store.state.new_survey;

      // Check that the survey's starting push message has all the info it needs
      if (survey.starting_message && survey.starting_message.push) {
        if (survey.starting_message.push.vars) {
          _.forEach(survey.starting_message.push.vars, (push_var, push_var_index) => {
            if (!push_var) {
              survey_errors.push(`push missing var _${push_var_index}`);
            };
          });
        };
        // NOTE: This shouldn't ever be an issue
        // if (survey.starting_message.push.type === 'image' && !survey.starting_message.push.image) {
        //   survey_errors.push(`push missing image`);
        // };
      };

      // Check that the push starting message has uploaded successfully
      if (survey.starting_message && survey.starting_message.push && survey.starting_message.push.image) {
        if (survey.starting_message.push.is_uploading) {
          survey_errors.push(`push image still uploading`);
        };
      };

      // Check that the survey has at least one survey question
      if (survey.questions.length === 0) {
        survey_errors.push('no questions');
      };

      // Check that each of the survey questions are formatted correctly.
      _.forEach(survey.questions, (question, question_index) => {

        // Check that all questions are formatted correctly
        if (question.text === '') {  // Check for translations on the server, fix there if need be.
          survey_errors.push(`question missing title _${question_index}`);
        };

        if (question.media && question.media.length) {  // Check whether question media has been uploaded properly
          for (let media of question.media) {
            if (media.is_uploading) {
              survey_errors.push(`question media still uploading _${question_index}`);
            };
          };
        };

        // Check that the answer type is correct
        if (!['video','image','audio','text','multi_choice','location','nps','multimedia','say'].includes(question.answer_type)) {
          survey_errors.push(`question wrong answer type _${question_index}`);
        };

        // Check that there are at least two valid multi_choice options
        if (question.answer_type === 'multi_choice') {
          let valid_multi_choice_option_count = 0;
          _.forEach(question.multi_choice, (option, option_index) => {
            if (option.text) {
              valid_multi_choice_option_count++;
            };
          });
          //if (valid_multi_choice_option_count < 2) {
          if (valid_multi_choice_option_count < 1) {
            survey_errors.push(`question missing valid options _${question_index}`);
          };
        };

      });

      return survey_errors;

    },

    prices() {
      return ({
        multi_choice: 0.02,
        video: 0.08,
        audio: 0.04,
        image: 0.04,
        multimedia: 0.08,
        text: 0.02,
        location: 0.04,
        nps: 0.02,
        say: 0.01
      });
    },


    estimate() {

      let estimate = {
        total_cost: 0,
        breakdown: {}
      };

      let questions = this.$store.state.new_survey.questions;

      let total_cost = 0;
      for (let question of questions) {
        let answer_type = question.answer_type;
        estimate.total_cost += this.prices[answer_type];
        if (estimate.breakdown[answer_type]) {
          estimate.breakdown[answer_type]++;
        } else {
          estimate.breakdown[answer_type] = 1;
        }
      };
      estimate.total_cost = estimate.total_cost.toFixed(2);
      return estimate;
    },

    languages() {
      return [
        { text: "🇬🇧 English", key: "en" },
        { text: "🇪🇸 Español", key: "es" },
        { text: "🇧🇷 Português", key: "pt" },
        { text: "🇮🇩 Bahasa",  key: "bh" },
        { text: "🇻🇳 Tiếng Việt",  key: "vi" },
        { text: "🇮🇳 हिन्दी",  key: "hi" },
        { text: "🌙 عربى",  key: "ar" },
        { text: "🇳🇱 Nederlands",  key: "nl" },
        { text: "🇫🇷 Français",  key: "fr" },
        { text: "🇩🇪 Deutsch",  key: "de" },
        { text: "🇮🇹 Italiano",  key: "it" },
        { text: "🇭🇷 Hrvatski",  key: "hr" },
        { text: "🇷🇸 Srpski",  key: "sr" },
      ]
    },

    dragOptions() {
      return {
        animation: 200,
        disabled: false,
      };
    },

    is_mobile() {
      return isMobileOnly;
    },

    is_editing() {
      return this.$route.params.is_editing === true;
    },

    is_wallet_hidden() {
      if (this.$store.state.user && this.$store.state.user.settings) {
        return this.$store.state.user.settings.is_wallet_hidden === true;
      } else {
        return false;
      };
    },


  },

  // Keyboard shortcuts
  mounted() {
    this.popupItem = this.$el
    window.scrollTo(0, 0);
  },

  methods: {

    updateSurveyTitle(event) {
      this.$store.commit('updateSurveyTitle', event.target.value);
    },

    selectLanguage(language) {
      this.$store.commit('updateSurveyLanguage', language);
    },

    pickSurveyEmoji(emoji) {
      if (emoji.native) {
        this.$store.commit('updateSurveyEmoji', emoji.native);
        this.is_emoji_picker_visible = false;
      };
    },

    hideEmojiPicker() {
      setTimeout(() => {
        this.is_emoji_picker_visible = false;
      }, 200);
    },

    exitButtonPressed: function() {
      this.$router.replace('/surveys');
      this.$store.commit('resetSurvey');
    },

    focusCard(index) {
      this.focused_block_index = index;
    },

    hideNewQuestionDropdown() {
      this.is_new_question_dropdown_visible = false;
    },

    create() {

      if (this.survey_errors.length > 0) {

        console.log(this.survey_errors);

        this.show_errors = true;
        setTimeout(() => {
          this.show_errors = false;
        }, 3500);

      } else {

        analytics.track('New Survey Created');

        this.is_submitting = true;

        let self = this;
        let survey_json = JSON.parse(JSON.stringify(this.$store.state.new_survey));

        survey_json.creator = {
          name: (this.$store.state.user.name || ''),
          profile_pic: (this.$store.state.user.profile_pic || ''),
          id: this.$store.state.user.uid
        };

        if (survey_json.title === '') {
          survey_json.title = `Survey #${this.$store.state.surveys.length}`
        };


        for (let i = 0; i < survey_json.questions.length; i++) {

          let question = survey_json.questions[i];

          // Replace the newline symbol to save in db.
          survey_json.questions[i].text = question.text.split(/\n|\r|↵/).join('\\n');

          // Remove blank multi_choice options
          if (question.answer_type === 'multi_choice') {
            survey_json.questions[i].multi_choice = _.filter(question.multi_choice, option => option.text);
          };

        };

        survey_json.is_published = false;
        survey_json.link_click_count = 0;


        this.$axios.post(`https://api-outsidevoice.vercel.app/upload_survey`, {
          survey_json,
          client_id: this.$store.state.user.uid
        }).then(res => {

          let data = res.data;

          let { survey_json, is_editing } = data;

          if (is_editing) {

            survey_json.index = this.$route.params.survey_index;
            this.$store.commit('editSurvey', { edited_survey: survey_json, survey_index: survey_json.index });

            // TODO: Add this back in.
            // setTimeout(() => { // Wait for edits to go through before resetting survey answers
            //   this.$store.dispatch('resetSurveyAnswers', survey_json.index);
            // }, 500);

            let survey_title = survey_json.title;
            this.$message.success(`Successfully edited survey: ${survey_title}`, 6);
            //this.$message.success(`Survey answers reset`, 4);

            this.$router.replace(`/surveys`);

          } else {

            survey_json.index = 0;

            let surveys = JSON.parse(JSON.stringify(this.$store.state.surveys));
            surveys.unshift(survey_json);
            this.$store.commit('initSurveys', surveys);
            this.$store.commit('updateSurveyIndexes');

            this.$store.dispatch('setNewSurveyRealtimeAnswerListener');

            let survey_title = survey_json.title;
            this.$message.success(`Successfully created survey: ${survey_title}`, 6);

            this.$router.replace(`/surveys`);

          };

          this.$store.commit('resetSurvey');
          self.is_submitting = false;


        }).catch(err => {
          console.log(err);
          // TODO: Display error message
        });
      };
    }

  },

  created() {
    analytics.page('New Survey');
  },

};

</script>

<style lang="scss" scoped>

.bg-fixed{
  position: fixed;
    top: 0;
    left: 0;
  //background-image: linear-gradient(135deg, #fefeff 1%,#fefeff 100%);
  background-color: rgb(245,245,245);
  min-height: 100%;
  min-width: 100%;
  z-index: -1;
}

section.main{
  padding-bottom: 50px;
  min-height: 100%;
}


// survey Title Input
input.survey-emoji{
  font-family: "Montserrat", sans-serif;
  color: rgb(60,60,60);
  width: 110px;
  text-align: center;
  background: transparent;
  font-size: 65px;
  text-align: center;
  font-weight: 600;
  background-color: rgb(238,238,238);
  border-radius: 6px;
  display: block;
  margin: 0 auto;
  margin-bottom: 25px;
  outline: none;
  border: none;
  border-radius: 6px;
  transition: all 0.3s ease;
  cursor: pointer;
  &:hover{
    background-color: rgb(234,234,234);
  }
}


.emoji-picker{
  position: absolute;
    top: 100px;
    left: 50%;
  transform: translateX(-100px);
  z-index: 5;
}

// survey Title Input
input.survey-title{
  font-family: "Montserrat", sans-serif;
  color: rgb(60,60,60);
  font-size: 30px;
  font-weight: 600;
  text-align: center;
  background: transparent;
  width: 100%;
  display: block;
  margin: 0 auto;
  margin-bottom: 25px;
  padding: 6px;
  border: none;
  border-radius: 4px;
  &:focus{
    outline: none;
  }
  &::placeholder{
    color: #B1BBC1;
  }
}


.new-question-container{
  display: block;
  margin: 0 auto;
  position: relative;
  width: 175px;

  button{
    height: 50px;
    padding-left: 52px;
    font-size: 17px;
    font-weight: 600;
    background-image: url("../assets/icons/question-btn-media-round.svg");
    background-color: transparent;
    background-size: 45px;
    background-repeat: no-repeat;
    background-position: center left;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    opacity: .8;
    &:hover{
      opacity: 1;
    }
    &:focus{
      outline: none;
    }
  }
  #new-question-dropdown{
    position: absolute;
      top: 55px;
      left: -120px;
    margin-bottom: 40px;
  }
}


.create-btn-container{
  button{
    color: white;
    font-size: 15px;
    font-weight: 600;
    padding: 7px 20px;
    background-color: rgb(200,200,200);
    border-color: rgb(200,200,200);
    border-width: 2px;
    border-radius: 4px;
    letter-spacing: 1px;
    cursor: pointer;
    &.active{
      background-color: #17C890;
      border-color: #16916A;
      transition: all 0.2s ease;
      &:hover{
        // TODO: animate box shadow
      }
    }
  }
  &.top{
    position: fixed;
      top: 42px;
      right: 40px;
  }
  &.bottom{
    display: block;
    margin: 0 auto;
    width: 100%;
    button{
      width: 100%;
    }
  }
  &.mobile{
    display: none;
  }
}

#language-dropdown{
  width: 120px;
  position: absolute;
    top: 45px;
    left: 50%;
  transform: translateX(-55px);
  display: flex;
  flex-direction: row;
  .anticon{
    margin-top: 8px;
    margin-left: 10px;
    opacity: .5;
    transition: all 0.2s ease;
    cursor: pointer;
    &:hover{
      opacity: .8;
    }
  }
}


#cost-estimator{
  position: fixed;
    bottom: 40px;
    left: 40px;
  padding: 14px 18px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid rgb(200,200,200);
  font-family: "Montserrat", sans-serif;
  cursor: default;
  p{
    font-size: 13px;
    margin: 0px;
    display: inline-block;
    margin-right: 12px;
  }
}

#escape-btn{
  color: rgb(30,30,30);
  position: fixed;
    top: 35px;
    left: 40px;
  background-color: transparent;
  display: block;
  font-size: 15px;
  font-weight: 600;
  padding: 10px 60px 12px 20px;
  margin: 0 auto;
  border: none;
  background-image: url("../assets/icons/close-btn.svg");
  background-repeat: no-repeat;
  background-position: 74px 14px;
  background-size: 14px 14px;
  cursor: pointer;
  font-family: "Montserrat", sans-serif;
  background-color: transparent;
  &:focus{
    outline: none;
  }
}


.container{
  margin: 0 auto;
  margin-top: 155px;
  margin-bottom: 430px;
  width: 600px;
  .section{
    margin-bottom: 70px;
  }
}



@media only screen
and (min-device-width : 320px)
and (max-device-width : 480px) {

#escape-btn,
.create-btn-container.top{
  position: absolute;
}

#escape-btn{
  left: 10px;
}

.create-btn-container.top{
  right: 17px;
  button{
    padding: 5px 15px;
    border: none;
  }
}

.create-btn-container.mobile{
  margin-top: 50px;
  display: block;
}

#language-dropdown{
  top: 42px;
}

.container{
  width: 100%;
  padding: 20px;
  margin-top: 100px;
  transform: translateX(0px);
}

input.survey-title{
  font-size: 24px;
  width: 100%;
  margin-bottom: 35px;
}

input.survey-emoji{
  font-size: 45px;
  width: 80px;
}

.new-question-container{
  width: 100%;
  & > button{
    display: block;
    margin: 0 auto;
  }
  #new-question-dropdown{
    left: 50%;
    transform: translateX(-85px);
  }
}

#cost-estimator{
  display: none;
}


}


</style>
